import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGoogleMaps } from '@shared/util/googleMaps';
import { GooglePlacesAutocompleteOptions, GooglePlacesAutocompleteSuggestion } from './types';

export function useGooglePlacesAutocomplete(
  query: string,
  { type, limit }: GooglePlacesAutocompleteOptions = {},
): {
  loading: boolean;
  suggestions: GooglePlacesAutocompleteSuggestion[];
} {
  const googleMaps = useGoogleMaps();

  const [loading, setLoading] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query), 500);
    return () => clearTimeout(timeout);
  }, [query]);

  const [suggestions, setSuggestions] = useState<GooglePlacesAutocompleteSuggestion[]>([]);

  const requestAutocompleteSuggestions = useCallback(async () => {
    if (!googleMaps) {
      return;
    }
    setLoading(true);
    try {
      const response = await new googleMaps.places.AutocompleteService().getPlacePredictions({
        input: debouncedQuery,
        types: !!type ? [type] : undefined,
      });
      if (!response) {
        throw new Error('Google Maps Places API autocomplete returned no response');
      }

      const newSuggestions = response.predictions
        .slice(0, limit)
        .map((p): GooglePlacesAutocompleteSuggestion => ({
          title: p.structured_formatting.main_text,
          subtitle: p.structured_formatting.secondary_text,
          googlePlaceId: p.place_id,
        }));
      setSuggestions(newSuggestions);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, type, limit, googleMaps]);

  useEffect(() => {
    if (!!googleMaps && !!debouncedQuery && debouncedQuery.length >= 3) {
      requestAutocompleteSuggestions();
    }
  }, [debouncedQuery, googleMaps, requestAutocompleteSuggestions]);

  useEffect(() => {
    if (!query) {
      setSuggestions([]);
    }
  }, [query]);

  return { loading, suggestions };
}

export function useFetchGooglePlacesCoords(): (
  googlePlaceId: string,
) => Promise<{ lat: number; lon: number }> {
  const googleMaps = useGoogleMaps();
  const div = useMemo(() => document.createElement('div'), []);
  return useCallback(
    async googlePlaceId => {
      if (!googleMaps) {
        return { lat: 0, lon: 0 };
      }
      return new Promise(resolve => {
        new googleMaps.places.PlacesService(div).getDetails({ placeId: googlePlaceId }, response =>
          resolve({
            lat: response?.geometry?.location?.lat() ?? 0,
            lon: response?.geometry?.location?.lng() ?? 0,
          }),
        );
      });
    },
    [googleMaps, div],
  );
}
